import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CLIENT_VIEW_GEOLOCATION_EVENTS } from "../../constants/clientViewLocalAnalyticsEvents";
import { useClientView } from "./ClientViewProvider";
import {
  GEOLOCATION_STATES,
  useClientViewGeolocation,
} from "../hooks/useClientViewGeolocation";
import { trackClientViewGeolocationEvent } from "../utils/clientViewGeolocationAnalytics";

const ClientViewGeolocationContext = createContext(null);

export function ClientViewGeolocationProvider({ children }) {
  const { slug, isPreview, analyticsEnabled } = useClientView();
  const disabled = isPreview;
  const geo = useClientViewGeolocation({ disabled });
  const [sharingEnabled, setSharingEnabled] = useState(null);
  const [sharingReady, setSharingReady] = useState(false);
  const trackedRef = useRef({
    requested: false,
    outcome: null,
  });

  const requestLocation = useCallback((options = {}) => {
    if (disabled) return;
    if (!options.force && geo.locationState === GEOLOCATION_STATES.FOUND) return;

    if (!trackedRef.current.requested) {
      trackedRef.current.requested = true;
      trackClientViewGeolocationEvent(slug, CLIENT_VIEW_GEOLOCATION_EVENTS.REQUESTED, {
        analyticsEnabled,
      });
    }

    geo.requestCurrentLocation(options);
  }, [analyticsEnabled, disabled, geo, slug]);

  const applySharingPreference = useCallback((enabled) => {
    if (enabled === true) setSharingEnabled(true);
    else if (enabled === false) setSharingEnabled(false);
    else setSharingEnabled(null);
    setSharingReady(true);
    if (enabled === false) {
      geo.clearLocation();
    }
  }, [geo]);

  const trackOutcome = useCallback(
    (eventType, payload = {}) => {
      if (trackedRef.current.outcome === eventType) return;
      trackedRef.current.outcome = eventType;
      trackClientViewGeolocationEvent(slug, eventType, {
        ...payload,
        analyticsEnabled,
      });
    },
    [analyticsEnabled, slug]
  );

  useEffect(() => {
    if (!trackedRef.current.requested) return;

    if (
      geo.locationState === GEOLOCATION_STATES.FOUND &&
      geo.coordinates &&
      trackedRef.current.outcome !== CLIENT_VIEW_GEOLOCATION_EVENTS.GRANTED
    ) {
      trackOutcome(CLIENT_VIEW_GEOLOCATION_EVENTS.GRANTED, {
        latitude: geo.coordinates.latitude,
        longitude: geo.coordinates.longitude,
        accuracyMeters: geo.coordinates.accuracy,
        method: geo.coordinates.method || "gps",
      });
      return;
    }

    if (
      geo.locationState === GEOLOCATION_STATES.DENIED &&
      trackedRef.current.outcome !== CLIENT_VIEW_GEOLOCATION_EVENTS.DENIED
    ) {
      trackOutcome(CLIENT_VIEW_GEOLOCATION_EVENTS.DENIED, { errorCode: 1 });
      return;
    }

    if (
      geo.locationState === GEOLOCATION_STATES.UNAVAILABLE &&
      trackedRef.current.outcome !== CLIENT_VIEW_GEOLOCATION_EVENTS.UNAVAILABLE
    ) {
      trackOutcome(CLIENT_VIEW_GEOLOCATION_EVENTS.UNAVAILABLE, {
        errorCode: navigator.geolocation ? "timeout_or_position_unavailable" : "unsupported",
      });
    }
  }, [geo.coordinates, geo.locationState, trackOutcome]);

  const value = useMemo(
    () => ({
      ...geo,
      requestLocation,
      applySharingPreference,
      sharingEnabled,
      sharingReady,
      disabled,
    }),
    [applySharingPreference, disabled, geo, requestLocation, sharingEnabled, sharingReady]
  );

  return (
    <ClientViewGeolocationContext.Provider value={value}>
      {children}
    </ClientViewGeolocationContext.Provider>
  );
}

export function useClientViewGeolocationContext() {
  const context = useContext(ClientViewGeolocationContext);
  if (!context) {
    throw new Error("useClientViewGeolocationContext must be used within ClientViewGeolocationProvider");
  }
  return context;
}

export function useOptionalClientViewGeolocation() {
  return useContext(ClientViewGeolocationContext);
}
